import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Bot, CalendarClock, CheckCircle2, Copy, Inbox, Loader2, Mail, Plus,
    RefreshCcw, Send, Sparkles, Trash2, XCircle,
} from "lucide-react";
import { API, api, formatApiError } from "../lib/api";

const DOC_CATEGORY_LABELS = {
    befattningsbeskrivning: "Befattningsbeskrivning",
    organisationsbeskrivning: "Organisationsbeskrivning",
    schema: "Schema / arbetsordning",
    annat: "Annat",
};

const STATUS = {
    aktiv: { label: "Aktiv", cls: "text-buoy border-buoy/40 bg-buoy/10" },
    utgangen: { label: "Utgången", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
    avslutad: { label: "Avslutad", cls: "text-current border-current/40 bg-current/10" },
};

const emptyForm = {
    participant_name: "", participant_email: "", role_id: "", role_label: "",
    department_node_id: "", duration_days: 14, diary_interval_days: 2, questions: "",
    send_invite: false,
};

function StatusBadge({ status }) {
    const s = STATUS[status] || STATUS.aktiv;
    return <span data-testid={`agent-status-${status}`} className={`font-mono text-[9px] uppercase tracking-widest rounded-full border px-2 py-0.5 ${s.cls}`}>{s.label}</span>;
}

function SynthesisView({ agent, onGenerate, onReview, generating }) {
    const s = agent.synthesis;
    return (
        <div className="rounded-xl border border-aidraft/30 bg-aidraft/5 p-4" data-testid="agent-synthesis">
            <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-aidraft">AI-sammanställning</p>
                    <p className="text-xs text-slate-500 mt-0.5">Utkast – kräver konsultens granskning</p>
                </div>
                <button data-testid="ai-synthesis-generate-button" onClick={onGenerate} disabled={generating}
                    className="flex items-center gap-1.5 rounded-lg bg-aidraft/15 border border-aidraft/40 text-aidraft px-3 py-2 text-xs font-semibold hover:bg-aidraft/25 disabled:opacity-50">
                    {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {s ? "Generera om" : "Generera"}
                </button>
            </div>
            {agent.synthesis_status === "pending" && <p className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sammanställningen skapas i bakgrunden …</p>}
            {agent.synthesis_status === "fel" && <p data-testid="synthesis-error" className="text-xs text-red-400">Genereringen misslyckades – försök igen.</p>}
            {s && (
                <div className="space-y-3 text-xs leading-relaxed">
                    <p className="text-slate-200">{s.sammanfattning}</p>
                    {s.formellt_uppdrag && <div><p className="font-semibold text-slate-300">Formellt uppdrag</p><p className="text-slate-400">{s.formellt_uppdrag}</p></div>}
                    {(s.faktiskt_arbete || []).length > 0 && <div><p className="font-semibold text-slate-300">Faktiskt arbete</p>{s.faktiskt_arbete.map((x, i) => <p key={i} className="text-slate-400">· {x}</p>)}</div>}
                    {(s.osynligt_arbete || []).length > 0 && <div><p className="font-semibold text-slate-300">Osynligt arbete</p>{s.osynligt_arbete.map((x, i) => <p key={i} className="text-amber-200/80">· {x}</p>)}</div>}
                    {(s.skillnader_formellt_faktiskt || []).length > 0 && <div><p className="font-semibold text-slate-300">Skillnad formellt/faktiskt</p>{s.skillnader_formellt_faktiskt.map((x, i) => <p key={i} className="text-slate-400">· {x}</p>)}</div>}
                    {(s.uppfoljningsfragor || []).length > 0 && <div><p className="font-semibold text-slate-300">Följdfrågor</p>{s.uppfoljningsfragor.map((x, i) => <p key={i} className="text-slate-400">· {x}</p>)}</div>}
                    {!agent.synthesis_reviewed && (
                        <button data-testid="ai-synthesis-approve-button" onClick={onReview}
                            className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-2 font-semibold hover:bg-buoy/25">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Markera som granskad
                        </button>
                    )}
                    {agent.synthesis_reviewed && <p className="font-mono text-[10px] uppercase tracking-widest text-buoy">Granskad av konsult</p>}
                </div>
            )}
        </div>
    );
}

export default function RoleAgents() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [project, setProject] = useState(null);
    const [projects, setProjects] = useState([]);
    const [agents, setAgents] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [creating, setCreating] = useState(false);
    const [newLink, setNewLink] = useState("");
    const [filter, setFilter] = useState("alla");
    const [endDate, setEndDate] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [sendingReply, setSendingReply] = useState(false);
    const [inbox, setInbox] = useState({ items: [], total_unread: 0 });
    const [sendingInvite, setSendingInvite] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadAgents = async () => {
        const r = await api.get(`/projects/${projectId}/role-agents`);
        setAgents(r.data);
        api.get(`/projects/${projectId}/consultant-inbox`).then((ir) => setInbox(ir.data)).catch(() => {});
        return r.data;
    };
    const openAgent = async (id) => {
        const r = await api.get(`/role-agents/${id}`);
        setSelected(r.data);
        if (r.data.unread_for_consultant_count > 0) {
            api.post(`/role-agents/${id}/mark-inbox-read`).then(loadAgents).catch(() => {});
        }
    };

    useEffect(() => {
        Promise.all([
            api.get("/projects"),
            api.get(`/projects/${projectId}/rollmatris`),
            api.get(`/projects/${projectId}/role-agents`),
            api.get(`/projects/${projectId}/consultant-inbox`),
        ]).then(([p, r, a, ib]) => {
            setProjects(p.data);
            setProject(p.data.find((x) => x.id === projectId));
            setRoles(r.data.rows || []);
            setAgents(a.data);
            setInbox(ib.data);
            setSelected(null);
            setNewLink("");
            const wantedAgent = searchParams.get("agent");
            if (wantedAgent && a.data.some((x) => x.id === wantedAgent)) openAgent(wantedAgent);
        }).catch((e) => toast.error(formatApiError(e, "Kunde inte ladda rollspeglingen"))).finally(() => setLoading(false));
    }, [projectId]);

    useEffect(() => { setEndDate(selected?.end_at?.slice(0, 10) || ""); setConfirmDelete(false); }, [selected?.id, selected?.end_at]);
    useEffect(() => {
        if (selected?.synthesis_status !== "pending") return;
        const t = setInterval(() => openAgent(selected.id).catch(() => {}), 5000);
        return () => clearInterval(t);
    }, [selected?.id, selected?.synthesis_status]);

    const filtered = useMemo(() => agents.filter((a) => filter === "alla" || a.status_live === filter), [agents, filter]);
    const counts = useMemo(() => agents.reduce((acc, a) => ({ ...acc, [a.status_live]: (acc[a.status_live] || 0) + 1 }), {}), [agents]);

    const chooseRole = (rollId) => {
        const row = roles.find((r) => r.roll_id === rollId);
        setForm((f) => ({ ...f, role_id: rollId, role_label: row?.namn || f.role_label, department_node_id: row?.hemma_id || "" }));
    };

    const createAgent = async () => {
        setCreating(true);
        try {
            const r = await api.post(`/projects/${projectId}/role-agents`, {
                participant_name: form.participant_name,
                participant_email: form.participant_email,
                role_label: form.role_label,
                department_node_id: form.department_node_id || null,
                role_id: form.role_id || null,
                duration_days: Number(form.duration_days),
                diary_interval_days: Number(form.diary_interval_days),
                prepared_questions: form.questions.split("\n").map((q) => q.trim()).filter(Boolean),
                send_invite: form.send_invite,
            });
            const url = `${window.location.origin}${r.data.participant_path}`;
            setNewLink(url);
            setForm(emptyForm);
            await loadAgents();
            await openAgent(r.data.id);
            toast.success("Rollagent skapad – kopiera den säkra länken");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte skapa rollagenten"));
        } finally {
            setCreating(false);
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Länk kopierad till urklipp");
        } catch {
            toast.error("Kunde inte kopiera – markera länken manuellt");
        }
    };

    const updateSelected = async (payload, message = "Rollagenten uppdaterad") => {
        try {
            const r = await api.put(`/role-agents/${selected.id}`, payload);
            setSelected(r.data);
            await loadAgents();
            toast.success(message);
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte uppdatera rollagenten"));
        }
    };

    const regenerate = async () => {
        try {
            const r = await api.post(`/role-agents/${selected.id}/regenerate-link`);
            const url = `${window.location.origin}${r.data.participant_path}`;
            setNewLink(url);
            toast.success("Ny säker länk skapad – gamla länken är ogiltig");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte skapa ny länk"));
        }
    };

    const sendInvite = async () => {
        setSendingInvite(true);
        try {
            const r = await api.post(`/role-agents/${selected.id}/send-invite`);
            const url = `${window.location.origin}${r.data.participant_path}`;
            setNewLink(url);
            await openAgent(selected.id);
            toast.success("Inbjudan skickad via e-post – tidigare länkar har ogiltigförklarats");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte skicka inbjudan"));
        } finally {
            setSendingInvite(false);
        }
    };

    const deleteAgent = async () => {
        try {
            await api.delete(`/role-agents/${selected.id}`);
            setSelected(null);
            await loadAgents();
            toast.success("Rollagenten och dess data raderades");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte radera rollagenten"));
        }
    };

    const generateSynthesis = async () => {
        try {
            await api.post(`/role-agents/${selected.id}/synthesis`);
            setSelected((s) => ({ ...s, synthesis_status: "pending" }));
            toast.info("AI-sammanställningen genereras i bakgrunden");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte starta sammanställningen"));
        }
    };

    const sendReply = async () => {
        const text = replyText.trim();
        if (!text || sendingReply) return;
        setSendingReply(true);
        try {
            await api.post(`/role-agents/${selected.id}/consultant-reply`, { meddelande: text });
            setReplyText("");
            await openAgent(selected.id);
            toast.success("Svaret skickades till deltagaren");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte skicka svaret"));
        } finally {
            setSendingReply(false);
        }
    };

    const deleteDoc = async (docId) => {
        try {
            await api.delete(`/role-agents/${selected.id}/documents/${docId}`);
            await openAgent(selected.id);
            toast.success("Dokumentet raderades");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte radera dokumentet"));
        }
    };

    if (loading) return <div className="min-h-screen bg-abyss flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-current" /></div>;

    return (
        <div className="min-h-screen bg-abyss text-slate-100" data-testid="rollspegling-page">
            <header className="h-16 border-b border-border bg-canvas/80 backdrop-blur-md flex items-center gap-3 px-4 xl:px-6" data-testid="consultant-hub-header">
                <Link to={`/karta?projekt=${projectId}`} data-testid="rollspegling-back" className="flex items-center gap-2 text-slate-400 hover:text-current transition-colors"><ArrowLeft className="w-4 h-4" /><span className="text-xs">Tillbaka</span></Link>
                <img src="/hamn-emblem.png" alt="HAMN" className="w-7 h-7 rounded-full border border-current/30 object-cover" />
                <div>
                    <h1 className="font-display font-extrabold text-xl tracking-tight">Rollspegling</h1>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{project?.name} · rollagenter & arbetsdagböcker</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500 hidden sm:inline">Projekt:</span>
                    <select data-testid="rollspegling-project-select" value={projectId || ""}
                        onChange={(e) => navigate(`/rollspegling/${e.target.value}`)}
                        className="bg-abyss/60 border border-current/40 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 outline-none focus:border-current/70 max-w-[200px] xl:max-w-[260px]">
                        {projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}{p.demo ? " (demo)" : ""}</option>
                        ))}
                    </select>
                </div>
            </header>

            <main className="grid grid-cols-1 xl:grid-cols-[390px_1fr] gap-5 p-4 xl:p-6">
                <aside className="space-y-4">
                    <section className="glass-panel rounded-2xl p-4" data-testid="invite-panel">
                        <h2 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Plus className="w-4 h-4 text-buoy" /> Ny rollagent</h2>
                        <p className="text-xs text-slate-500 mb-1">En säker länk skapas. Skicka den via din egen skyddade kanal.</p>
                        <p className="text-[11px] mb-4 rounded-lg border border-current/30 bg-current/5 px-2.5 py-1.5" data-testid="invite-project-binding">
                            <span className="text-slate-400">Inbjudan kopplas till projekt: </span>
                            <strong className="text-current">{project?.name}{project?.demo ? " (demo)" : ""}</strong>
                        </p>
                        <div className="space-y-3">
                            <input data-testid="invite-modal-role-input" className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-current/60" placeholder="Namn eller beteckning" value={form.participant_name} onChange={(e) => setForm({ ...form, participant_name: e.target.value })} />
                            <input data-testid="invite-modal-email-input" type="email" className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-current/60" placeholder="E-postadress" value={form.participant_email} onChange={(e) => setForm({ ...form, participant_email: e.target.value })} />
                            <select data-testid="invite-existing-role-select" className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-current/60" value={form.role_id} onChange={(e) => chooseRole(e.target.value)}>
                                <option value="">Koppla till kartlagd roll …</option>
                                {roles.map((r) => <option key={r.roll_id} value={r.roll_id}>{r.namn} · {r.hemma_label}</option>)}
                            </select>
                            <input data-testid="invite-role-label-input" className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-current/60" placeholder="Roll att spegla" value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} />
                            <div className="grid grid-cols-2 gap-2">
                                <label className="text-xs text-slate-400">Period (dagar)<input data-testid="invite-modal-duration-select" type="number" min="1" max="120" className="mt-1 w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} /></label>
                                <label className="text-xs text-slate-400">Dagbok var n:te dag<input data-testid="invite-diary-interval-input" type="number" min="1" max="14" className="mt-1 w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none" value={form.diary_interval_days} onChange={(e) => setForm({ ...form, diary_interval_days: e.target.value })} /></label>
                            </div>
                            <textarea data-testid="invite-questions-input" rows="5" className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-current/60" placeholder="Konsultens frågor – en per rad" value={form.questions} onChange={(e) => setForm({ ...form, questions: e.target.value })} />
                            <label className="flex items-start gap-2.5 text-xs text-slate-300 cursor-pointer" data-testid="invite-send-email-label">
                                <input data-testid="invite-send-email-checkbox" type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} className="mt-0.5 accent-cyan-400" />
                                Skicka inbjudan direkt via e-post (säker länk, inget innehåll i mejlet)
                            </label>
                            <button data-testid="invite-modal-submit-button" onClick={createAgent} disabled={creating || !form.participant_name || !form.participant_email || !form.role_label}
                                className="w-full flex items-center justify-center gap-2 rounded-lg bg-current text-abyss font-semibold text-sm py-2.5 hover:bg-tide disabled:opacity-40">
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />} Skapa säker inbjudan
                            </button>
                        </div>
                    </section>

                    {newLink && (
                        <section className="rounded-2xl border border-buoy/40 bg-buoy/10 p-4" data-testid="secure-link-panel">
                            <p className="font-mono text-[9px] uppercase tracking-widest text-buoy mb-2">Säker deltagarlänk</p>
                            <p className="break-all font-mono text-[11px] text-slate-300 mb-3">{newLink}</p>
                            <button data-testid="copy-secure-link-button" onClick={() => copy(newLink)} className="w-full flex items-center justify-center gap-2 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-2 text-xs font-semibold hover:bg-buoy/25"><Copy className="w-3.5 h-3.5" /> Kopiera länk</button>
                        </section>
                    )}

                    <section className="glass-panel rounded-2xl p-4">
                        {inbox.total_unread > 0 && (
                            <div className="mb-4 rounded-xl border border-violet-500/40 bg-violet-500/10 p-3" data-testid="consultant-inbox-panel">
                                <p className="font-mono text-[9px] uppercase tracking-widest text-violet-300 mb-2 flex items-center gap-1.5">
                                    <Inbox className="w-3.5 h-3.5" /> Inkorg · {inbox.total_unread} olästa från deltagare
                                </p>
                                <div className="space-y-1.5">
                                    {inbox.items.map((item) => (
                                        <button key={item.agent_id} data-testid={`inbox-item-${item.agent_id}`} onClick={() => openAgent(item.agent_id)}
                                            className="w-full text-left rounded-lg border border-violet-500/20 bg-abyss/40 px-2.5 py-2 hover:border-violet-400/50 transition-colors">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-xs font-semibold text-slate-200">{item.participant_name}</span>
                                                <span className="font-mono text-[9px] text-violet-300">{item.unread} nya</span>
                                            </div>
                                            {item.latest_message && <p className="text-[11px] text-slate-400 truncate mt-0.5">{item.latest_message.content}</p>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex gap-1.5 mb-3" data-testid="agent-filter-tabs">
                            {["alla", "aktiv", "utgangen", "avslutad"].map((f) => (
                                <button key={f} data-testid={`filter-tab-${f}`} onClick={() => setFilter(f)} className={`rounded-full border px-2.5 py-1 text-[10px] ${filter === f ? "border-current/50 text-current bg-current/10" : "border-border text-slate-500"}`}>
                                    {f === "alla" ? "Alla" : STATUS[f].label}{f !== "alla" && counts[f] ? ` (${counts[f]})` : ""}
                                </button>
                            ))}
                        </div>
                        <div className="space-y-2">
                            {filtered.map((a) => (
                                <button key={a.id} data-testid={`agent-card-${a.id}`} onClick={() => openAgent(a.id)}
                                    className={`w-full text-left rounded-xl border p-3 transition-colors ${selected?.id === a.id ? "border-current/50 bg-current/10" : "border-border bg-deepsea/40 hover:border-current/30"}`}>
                                    <div className="flex items-center justify-between gap-2"><span className="font-semibold text-sm">{a.participant_name}</span><div className="flex items-center gap-1.5">{a.unread_for_consultant_count > 0 && <span data-testid={`unread-badge-${a.id}`} className="w-5 h-5 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center">{a.unread_for_consultant_count}</span>}<StatusBadge status={a.status_live} /></div></div>
                                    <p className="text-xs text-slate-400 mt-1">{a.role_label}{a.department_label ? ` · ${a.department_label}` : ""}</p>
                                    <p className="font-mono text-[9px] text-slate-600 mt-1">{a.diary_count} dagböcker · {a.message_count} meddelanden · t.o.m. {a.end_at.slice(0, 10)}</p>
                                </button>
                            ))}
                            {!filtered.length && <p className="text-xs text-slate-500 text-center py-5">Inga rollagenter i filtret.</p>}
                        </div>
                    </section>
                </aside>

                <section className="glass-panel rounded-2xl min-h-[70vh] flex flex-col" data-testid="agent-detail-panel">
                    {!selected ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                            <Bot className="w-10 h-10 text-current mb-3" />
                            <h2 className="font-display font-bold text-xl">Välj eller skapa en rollagent</h2>
                            <p className="text-sm text-slate-500 mt-2 max-w-md">Varje deltagare får en egen digital följeslagare som samlar formellt uppdrag, faktiskt arbete, kontakter och osynliga insatser.</p>
                        </div>
                    ) : (
                        <>
                            <div className="border-b border-border p-4 xl:p-5">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-2"><h2 className="font-display font-bold text-2xl" data-testid="agent-detail-name">{selected.participant_name}</h2><StatusBadge status={selected.status_live} /></div>
                                        <p className="text-sm text-slate-400 mt-1">{selected.role_label}{selected.department_label ? ` · ${selected.department_label}` : ""} · {selected.participant_email}</p>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        {!selected.consent_at && (
                                            <button data-testid="agent-send-invite" onClick={sendInvite} disabled={sendingInvite}
                                                className="flex items-center gap-1.5 rounded-lg border border-buoy/40 bg-buoy/10 text-buoy px-3 py-2 text-xs hover:bg-buoy/20 disabled:opacity-50">
                                                {sendingInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Skicka inbjudan
                                            </button>
                                        )}
                                        <button data-testid="agent-regenerate-link" onClick={regenerate} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs text-slate-300 hover:border-current/50"><RefreshCcw className="w-3.5 h-3.5" /> Ny länk</button>
                                        {selected.status_live === "aktiv" ? <button data-testid="agent-close-button" onClick={() => updateSelected({ status: "avslutad" }, "Kontakten avslutades")} className="flex items-center gap-1.5 rounded-lg border border-amber-500/40 text-amber-300 px-3 py-2 text-xs"><XCircle className="w-3.5 h-3.5" /> Avsluta</button> : null}
                                        {!confirmDelete ? <button data-testid="agent-delete-button" onClick={() => setConfirmDelete(true)} className="flex items-center gap-1.5 rounded-lg border border-red-500/40 text-red-400 px-3 py-2 text-xs"><Trash2 className="w-3.5 h-3.5" /> Radera</button> : <button data-testid="agent-delete-confirm" onClick={deleteAgent} className="rounded-lg bg-red-500/20 border border-red-500/50 text-red-300 px-3 py-2 text-xs font-semibold">Bekräfta radering</button>}
                                    </div>
                                </div>
                                <div className="mt-4 flex items-end gap-2 flex-wrap">
                                    <label className="text-xs text-slate-400">Slutdatum<input data-testid="agent-end-date-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="ml-2 bg-abyss/60 border border-border rounded-lg px-2 py-1.5 text-xs" /></label>
                                    <button data-testid="agent-save-end-date" onClick={() => updateSelected({ end_at: `${endDate}T23:59:59+00:00` }, "Perioden uppdaterades")} className="rounded-lg border border-border px-3 py-2 text-xs text-slate-300 hover:border-current/50"><CalendarClock className="w-3.5 h-3.5 inline mr-1" /> Spara period</button>
                                    <span className="font-mono text-[10px] text-slate-500">Samtycke: {selected.consent_at ? selected.consent_at.slice(0, 10) : "saknas"}</span>
                                    {selected.invite_sent_at && <span className="font-mono text-[10px] text-buoy" data-testid="invite-sent-badge">Inbjudan mejlad {selected.invite_sent_at.slice(0, 10)}</span>}
                                    {selected.reminder_count > 0 && <span className="font-mono text-[10px] text-slate-500" data-testid="reminder-count-badge">{selected.reminder_count} påminnelser</span>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 2xl:grid-cols-[1fr_380px] flex-1 min-h-0">
                                <div className="p-4 xl:p-5 overflow-y-auto panel-scroll" data-testid="audit-trail-view">
                                    <h3 className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Granskningstråd</h3>
                                    <div className="space-y-3">
                                        {(selected.messages || []).map((m) => (
                                            <div key={m.id} data-testid={`agent-message-${m.id}`} className={`rounded-xl border p-3 ${m.role === "consultant" ? "border-buoy/40 bg-buoy/10" : m.role === "assistant" ? "border-current/20 bg-current/5" : m.kind === "dagbok" ? "border-amber-500/30 bg-amber-500/5" : m.kind === "till_konsult" ? "border-violet-500/30 bg-violet-500/5" : "border-border bg-deepsea/40"}`}>
                                                <div className="flex justify-between gap-2 mb-1"><span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{m.role === "consultant" ? "Du (konsult)" : m.role === "assistant" ? "Rollagent" : "Deltagare"} · {m.kind}</span><span className="font-mono text-[9px] text-slate-600">{m.at?.slice(0, 16).replace("T", " ")}</span></div>
                                                <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{m.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 rounded-xl border border-buoy/30 bg-buoy/5 p-3" data-testid="consultant-reply-panel">
                                        <p className="font-mono text-[9px] uppercase tracking-widest text-buoy mb-2">Svara deltagaren</p>
                                        <p className="text-[11px] text-slate-500 mb-2">Svaret visas i deltagarens portal som "Konsulten" – inte som AI-agenten.</p>
                                        <textarea data-testid="agent-reply-input" rows="2" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                                            placeholder="Skriv ditt svar till deltagaren …"
                                            className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-xs outline-none focus:border-buoy/60" />
                                        <button data-testid="agent-reply-submit" onClick={sendReply} disabled={sendingReply || !replyText.trim()}
                                            className="mt-2 flex items-center gap-1.5 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy px-3 py-2 text-xs font-semibold hover:bg-buoy/25 disabled:opacity-40">
                                            {sendingReply ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Skicka svar
                                        </button>
                                    </div>
                                </div>
                                <aside className="border-t 2xl:border-t-0 2xl:border-l border-border p-4 xl:p-5 overflow-y-auto panel-scroll">
                                    {(selected.documents || []).length > 0 && (
                                        <div className="mb-4 rounded-xl border border-border bg-deepsea/30 p-3" data-testid="agent-documents-panel">
                                            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Dokument från deltagaren ({selected.documents.length})</p>
                                            <div className="space-y-1.5">
                                                {selected.documents.map((d) => (
                                                    <div key={d.id} data-testid={`agent-document-${d.id}`} className="flex items-center gap-2 rounded-lg border border-border bg-abyss/40 px-2.5 py-2">
                                                        <a href={`${API}/role-agents/${selected.id}/documents/${d.id}/download`}
                                                            data-testid={`agent-document-download-${d.id}`}
                                                            className="flex-1 min-w-0 text-[11px] text-slate-300 truncate hover:text-current">{d.filename}</a>
                                                        <span className="font-mono text-[8px] uppercase tracking-widest text-slate-500 shrink-0">{DOC_CATEGORY_LABELS[d.kategori] || d.kategori}</span>
                                                        <button data-testid={`agent-document-delete-${d.id}`} onClick={() => deleteDoc(d.id)}
                                                            className="text-slate-600 hover:text-red-400 shrink-0" title="Radera dokument">
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <SynthesisView agent={selected} generating={selected.synthesis_status === "pending"} onGenerate={generateSynthesis}
                                        onReview={() => api.post(`/role-agents/${selected.id}/synthesis/review`).then(() => openAgent(selected.id)).then(loadAgents).catch((e) => toast.error(formatApiError(e)))} />
                                </aside>
                            </div>
                        </>
                    )}
                </section>
            </main>
        </div>
    );
}
